import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { ArchiveSearchResult, OutboundDraft, DraftDecisionResult } from '../types/domain';

const DRAFTS_KEY = ['drafts'] as const;

export function useDraftsList(status: string = 'PendingApproval') {
  return useQuery({
    queryKey: [...DRAFTS_KEY, 'list', status],
    queryFn: () => api.get<OutboundDraft[]>(`/api/v1/drafts?status=${encodeURIComponent(status)}`),
  });
}

export function useApproveDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reviewNote }: { id: number; reviewNote?: string }) =>
      api.post<DraftDecisionResult>(`/api/v1/drafts/${id}/approve`, { reviewNote }),
    onSuccess: () => qc.invalidateQueries({ queryKey: DRAFTS_KEY }),
  });
}

export interface BulkApproveItem {
  draftId: number;
  outcome: 'Sent' | 'Skipped' | 'Failed' | 'NotPending' | 'NeedsIndividualReview';
  reason?: string;
  ticketNumbers?: string[];
}

export interface BulkApproveResult {
  summary: {
    requested: number; sent: number; skipped: number; failed: number; notPending: number;
    /** P24: carried a blocking warning and was NOT sent. */
    needsIndividualReview: number;
  };
  results: BulkApproveItem[];
}

/**
 * Approve several drafts in one call.
 *
 * Answers 200 even when some items failed — the per-item breakdown is the
 * point, so the caller MUST read `summary`/`results` rather than treating a
 * resolved promise as "all sent".
 */
export function useBulkApproveDrafts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftIds, reviewNote }: { draftIds: number[]; reviewNote?: string }) =>
      api.post<BulkApproveResult>('/api/v1/drafts/bulk-approve', { draftIds, reviewNote }),
    onSuccess: () => qc.invalidateQueries({ queryKey: DRAFTS_KEY }),
  });
}

export function useRejectDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reviewNote }: { id: number; reviewNote: string }) =>
      api.post<OutboundDraft>(`/api/v1/drafts/${id}/reject`, { reviewNote }),
    onSuccess: () => qc.invalidateQueries({ queryKey: DRAFTS_KEY }),
  });
}

export function useUpdateDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; subject?: string; bodyHtml?: string; toAddresses?: string[]; ccAddresses?: string[] | null }) =>
      api.put<OutboundDraft>(`/api/v1/drafts/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: DRAFTS_KEY }),
  });
}

/**
 * Change the document a draft carries (P24).
 *
 * Three actions, one endpoint, because they are one decision seen from three
 * sides: 'choose' promotes a candidate the search already found, 'remove'
 * leaves the reply carrying nothing, 'add' brings a document in from the
 * archive.
 *
 * `add` deliberately sends the QUERY and the FILE NAME rather than the URL the
 * browser already holds: the server looks the document up again in the index
 * and takes the fetch ids from its own result, so a client can never nominate
 * a file for the send path to download. See the endpoint's header.
 *
 * A 409 is the C9 guard, not a failure — the chosen document is not in the
 * language the request is owed and nobody has signed for that. Repeat the same
 * call with `acceptLanguageMismatch: true` after the person has confirmed.
 */
export interface AttachmentAction {
  id: number;
  action: 'choose' | 'remove' | 'add';
  /** 'choose' — the rank of a candidate already on the draft. */
  rank?: number;
  /** 'add' — the query that produced the list, and the file the person picked. */
  query?: string;
  fileName?: string;
  language?: string | null;
  acceptLanguageMismatch?: boolean;
}

export function useUpdateDraftAttachments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: AttachmentAction) =>
      api.put<OutboundDraft>(`/api/v1/drafts/${id}/attachments`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: DRAFTS_KEY }),
  });
}

/**
 * Search the official archive from the approval screen (P24, decision 5).
 *
 * Read-only, and `enabled` gated so nothing is searched while somebody is still
 * typing the first two letters. The index is the ONLY source an attachment may
 * come from — there is no upload, by decision.
 */
export function useArchiveSearch(q: string, language: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['documents', 'search', q, language],
    queryFn: () => api.get<ArchiveSearchResult>(
      `/api/v1/documents/search?q=${encodeURIComponent(q)}${language ? `&language=${encodeURIComponent(language)}` : ''}`,
    ),
    enabled: enabled && q.trim().length >= 2,
  });
}

/**
 * Put an approved-but-unsent draft back in the queue (P24).
 *
 * The state this unfreezes is real and ordinary: approved, then held by the
 * recipient-domain allowlist, and from then on uneditable, un-approvable and
 * un-replaceable. Draft 32 on DRA-2026-00031 has been sitting in it since
 * 2026-08-31.
 */
export function useReopenDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      api.post<OutboundDraft>(`/api/v1/drafts/${id}/reopen`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DRAFTS_KEY });
      qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}
